import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { generateMigrationGuide } from '../api/migration';
import { ApiVersionSummary } from '../api/releases';
import { queryKeys } from './keys';
import { useVersions } from './releases';

export const MIGRATION_POLL_INTERVAL_MS = 5_000;
export const MIGRATION_POLL_TIMEOUT_MS = 15 * 60 * 1_000;

export interface PendingGuide {
  /**
   * `migrationGuideS3Key` at the moment generation was kicked off. A *different*
   * key means this run produced a new guide, so a regeneration (where a key was
   * already present) settles correctly too.
   */
  keyAtKickoff?: string;
  /** Epoch ms after which we stop waiting. */
  deadline: number;
}

export type GuideOutcome = 'pending' | 'ready' | 'timed-out';

export interface GuideMessage {
  text: string;
  ok: boolean;
}

/**
 * Decides whether a generation run has finished, given the guide key currently
 * on the version record. Pure, so the state machine can be tested without a
 * component, a clock or a server.
 */
export function guideOutcome(
  pending: PendingGuide,
  currentKey: string | undefined,
  now: number,
): GuideOutcome {
  if (currentKey !== undefined && currentKey !== pending.keyAtKickoff) {
    return 'ready';
  }
  return now >= pending.deadline ? 'timed-out' : 'pending';
}

/**
 * Owns migration-guide generation: kicking a run off, polling for its result
 * and reporting the outcome per version.
 *
 * The poll is a second observer on the *same* versions query key. React Query
 * dedupes the request, so the page's own `useVersions()` re-renders with the
 * fresh rows for free — there is no interval bookkeeping, and no cleanup to
 * forget on unmount.
 */
export function useMigrationGuides(apiId: string) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<Record<string, PendingGuide>>({});
  const [messages, setMessages] = useState<Record<string, GuideMessage>>({});

  const waiting = Object.keys(pending).length > 0;

  // `dataUpdatedAt` — not just `data` — because structural sharing keeps the
  // data reference stable when a poll returns unchanged rows, and the timeout
  // still has to be noticed on those ticks.
  const { data: versions, dataUpdatedAt } = useVersions(apiId, {
    refetchInterval: waiting ? MIGRATION_POLL_INTERVAL_MS : false,
  });

  useEffect(() => {
    if (!versions) {
      return;
    }
    const now = Date.now();
    const settled = Object.entries(pending)
      .map(([versionId, entry]) => {
        const currentKey = versions.find((v) => v.versionId === versionId)?.migrationGuideS3Key;
        return [versionId, guideOutcome(entry, currentKey, now)] as const;
      })
      .filter(([, outcome]) => outcome !== 'pending');

    if (settled.length === 0) {
      return;
    }

    setPending((prev) => {
      const next = { ...prev };
      for (const [versionId] of settled) {
        delete next[versionId];
      }
      return next;
    });
    setMessages((prev) => {
      const next = { ...prev };
      for (const [versionId, outcome] of settled) {
        next[versionId] =
          outcome === 'ready'
            ? { text: 'Migration guide ready', ok: true }
            : { text: 'Still running — check back shortly', ok: false };
      }
      return next;
    });
  }, [versions, dataUpdatedAt, pending]);

  const { mutate: generate } = useMutation({
    mutationFn: (versionId: string) => generateMigrationGuide(apiId, versionId),
    onMutate: (versionId) => {
      const keyAtKickoff = queryClient
        .getQueryData<ApiVersionSummary[]>(queryKeys.versions(apiId))
        ?.find((v) => v.versionId === versionId)?.migrationGuideS3Key;

      setPending((prev) => ({
        ...prev,
        [versionId]: { keyAtKickoff, deadline: Date.now() + MIGRATION_POLL_TIMEOUT_MS },
      }));
      setMessages((prev) => ({
        ...prev,
        [versionId]: { text: 'Generating — may take a minute…', ok: true },
      }));
    },
    onError: (err, versionId) => {
      setPending((prev) => {
        const next = { ...prev };
        delete next[versionId];
        return next;
      });
      setMessages((prev) => ({ ...prev, [versionId]: { text: String(err), ok: false } }));
    },
  });

  return {
    generate,
    isGenerating: (versionId: string) => versionId in pending,
    messageFor: (versionId: string): GuideMessage | undefined => messages[versionId],
  };
}
